import { useEffect, useState } from 'react';
import PeerReviewDocumentViewer from './PeerReviewDocumentViewer';
import StructuredFormPeerReviewViewer from './StructuredFormPeerReviewViewer';


const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(response, label) {
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`${label} failed (${response.status}): ${detail}`);
  }
  return response.json();
}

async function fetchRows(path, label) {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: apiHeaders()
  });
  return readJson(response, label);
}

function studentName(enrolment) {
  const student = enrolment?.students;
  return (
    student?.english_name ||
    student?.chinese_name ||
    student?.student_code ||
    'Student'
  );
}

export default function ReceivedPeerReviews({ targetSubmissionId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!targetSubmissionId) {
        setItems([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const allocations = await fetchRows(
          `peer_review_allocations?target_submission_id=eq.${targetSubmissionId}&select=*`,
          'Loading received Peer Reviews'
        );

        const loaded = await Promise.all(
          (Array.isArray(allocations) ? allocations : []).map(
            async allocation => {
              const [reviewRows, configRows] = await Promise.all([
                fetchRows(
                  `peer_review_submissions?allocation_id=eq.${allocation.id}&submitted=eq.true&select=*&limit=1`,
                  'Loading submitted Peer Review'
                ),
                fetchRows(
                  `peer_review_configs?course_assignment_id=eq.${allocation.peer_review_course_assignment_id}&select=*&limit=1`,
                  'Loading Peer Review settings'
                )
              ]);

              const review = reviewRows?.[0];
              const config = configRows?.[0];

              if (!review || !config) return null;

              const comments = await fetchRows(
                `peer_review_comments?peer_review_submission_id=eq.${review.id}&select=*&order=page_number.asc,created_at.asc`,
                'Loading Peer Review comments'
              );

              let reviewer = null;
              if (!config.anonymous) {
                const reviewerRows = await fetchRows(
                  `enrolments?id=eq.${allocation.reviewer_enrolment_id}&select=id,students(student_code,english_name,chinese_name)&limit=1`,
                  'Loading peer reviewer'
                );
                reviewer = reviewerRows?.[0] || null;
              }

              return {
                allocation,
                review,
                config,
                comments: Array.isArray(comments) ? comments : [],
                reviewer
              };
            }
          )
        );

        if (!cancelled) {
          setItems(loaded.filter(Boolean));
        }
      } catch (err) {
        console.error('Received Peer Review load failed:', err);
        if (!cancelled) {
          setError(err?.message || 'Could not load received Peer Review.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [targetSubmissionId]);

  if (!targetSubmissionId || (!loading && !error && items.length === 0)) {
    return null;
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.heading}>Peer review</div>

      {loading && (
        <div style={styles.muted}>Loading peer feedback...</div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {items.map((item, index) => (
        <div key={item.review.id} style={styles.reviewBox}>
          <div style={styles.reviewHeader}>
            {item.config.anonymous
              ? 'Peer Reviewer'
              : `Reviewed by ${studentName(item.reviewer)}`}
            {items.length > 1 ? ` · Review ${index + 1}` : ''}
          </div>

                    {item.config?.target_structured_instance_id ? (
            <StructuredFormPeerReviewViewer
              targetSubmissionId={
                item.allocation.target_submission_id
              }
              comments={item.comments}
              editable={false}
            />
          ) : (
            <PeerReviewDocumentViewer
              allocationId={item.allocation.id}
              comments={item.comments}
              editable={false}
            />
          )}


          <div style={styles.overallBox}>
            <div style={styles.overallLabel}>Overall comments</div>
            <div style={styles.overallText}>
              {String(item.review.overall_comment || '').trim() ||
                'No overall comment.'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  wrap: {
    margin: '16px 0',
    paddingTop: '14px',
    borderTop: '1px solid #dce3e8'
  },
  heading: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '9px'
  },
  reviewBox: {
    border: '1px solid #dce3e8',
    borderRadius: '9px',
    padding: '12px',
    marginBottom: '14px',
    background: '#fff'
  },
  reviewHeader: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#52616d',
    marginBottom: '6px'
  },
  overallBox: {
    marginTop: '14px',
    paddingTop: '12px',
    borderTop: '1px solid #e1e7ec'
  },
  overallLabel: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#334e68',
    marginBottom: '6px'
  },
  overallText: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.55,
    color: '#334e68',
    fontSize: '13px'
  },
  muted: {
    fontSize: '12px',
    color: '#7a8894'
  },
  error: {
    padding: '8px 10px',
    border: '1px solid #efb7b7',
    borderRadius: '7px',
    background: '#fff4f4',
    color: '#912d2d',
    fontSize: '12px'
  }
};